import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetMatterV2 } from '../../../api/queries/useGetMatterV2';
import { useGetDocFactsV2 } from '@/api/queries/useGetDocFactsV2';
import { useGetFactV2 } from '@/api/queries/useGetFactV2';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { processFactHighlights } from '@/new/lib/doc-viewer-utils';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';

export const FactDocViewer = ({
  docId,
  factId,
  onClose,
  hideFeatureButtons,
  hideCloseButton,
  className,
}: {
  docId: string;
  factId?: string | null;
  onClose?: () => void;
  hideFeatureButtons?: boolean;
  hideCloseButton?: boolean;
  className?: string;
}) => {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const requestSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { closeDocViewer } = useDocumentNavigation();
  const setPopupFact = useStore((state) => state.setPopupFact);

  const [showFacts, setShowFacts] = useState(false);

  // Data fetching
  const { data: fact } = useGetFactV2(matterId, requestSubsetId, factId);
  const { data: documentFacts } = useGetDocFactsV2(matterId, requestSubsetId, docId);

  const { coordinates } = useMemo(() => {
    if (!fact) return { coordinates: [] };
    if (showFacts) {
      if (!documentFacts) return { coordinates: [] };
      return processFactHighlights(documentFacts, docId);
    }
    return processFactHighlights([fact], docId);
  }, [fact, documentFacts, docId, showFacts]);

  const handleHighlightClick = (id: string) => {
    if (factId) {
      const fact = documentFacts?.find((fact) => fact.id === id);
      if (fact) {
        setPopupFact(fact);
        // Copy source text to clipboard, find the source text for that docId and factId
        const location = fact.locations.find((location) => location.docId === docId);
        if (location) {
          navigator.clipboard.writeText(location?.sources?.map((source) => source.text).join('\n') ?? '');
          toast.success('Source text copied to clipboard');
        }
      }
    }
  };

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="flex-grow overflow-hidden">
        <div className="relative h-full">
          <PDFViewer
            docId={docId}
            highlights={coordinates}
            closeViewer={hideCloseButton ? undefined : (onClose ?? closeDocViewer)}
            highlightedFactId={factId}
            onHighlightClick={handleHighlightClick}
            hideFeatureButtons={hideFeatureButtons}
            showFacts={showFacts}
            setShowFacts={setShowFacts}
          />
        </div>
      </div>
    </div>
  );
};
